import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckIcon } from '@heroicons/react/24/outline';
import customFetch from '@utils/customFetch';
import NewUserSlideIn from '@components/dashboard/admin/benutzerverwaltung/newUserSlideIn';
import AdminListToolbar from '@components/dashboard/admin/shared/AdminListToolbar';
import Pagination from '@components/dashboard/admin/shared/Pagination';
import SortableHeader from '@components/dashboard/admin/shared/SortableHeader';
import StatusBadge, {
  statusFilterOptions,
} from '@components/dashboard/admin/shared/StatusBadge';
import useAdminListParams from '@/hooks/useAdminListParams';
import { formatDate } from '@utils/format';

const allUsersQuery = (searchParams) => ({
  queryKey: ['allUsers', searchParams.toString()],
  queryFn: async () => {
    const { data } = await customFetch.get(
      `/users/admin/all-users?${searchParams.toString()}`
    );
    return data;
  },
});

export const loader =
  (queryClient) =>
  async ({ request }) => {
    const { searchParams } = new URL(request.url);
    return queryClient.ensureQueryData(allUsersQuery(searchParams));
  };

const ROLE_OPTIONS = [
  { value: '', label: 'Alle Rollen' },
  { value: 'user', label: 'Benutzer' },
  { value: 'admin', label: 'Admin' },
];

const VERIFIED_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'true', label: 'Bestätigt' },
  { value: 'false', label: 'Offen' },
];

const COMPANY_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'true', label: 'Mit Firma' },
  { value: 'false', label: 'Ohne Firma' },
];

const COLUMN_COUNT = 9;

const UserList = () => {
  const { searchParams, page, limit, search, sort, getParam, setParams, setPage, reset } =
    useAdminListParams({ defaultSort: 'createdAt:desc' });

  const { data, isLoading, isError } = useQuery(allUsersQuery(searchParams));
  const users = data?.users ?? [];
  const totalUsers = data?.totalUsers ?? 0;
  const numOfPages = data?.numOfPages ?? 0;

  const [showSlideIn, setShowSlideIn] = useState(false);
  const queryClient = useQueryClient();

  const handleSort = (value) => setParams({ sort: value });

  const handleCreateUser = async (formData) => {
    const data = Object.fromEntries(formData);
    try {
      // Variante C: Benutzer direkt anlegen (kein Passwort nötig).
      const { data: res } = await customFetch.post('/users/admin/users', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        sendMail: data.sendMail === 'on',
        // Nur ohne Aktivierungs-E-Mail gesetzt (siehe NewUserSlideIn)
        ...(data.password ? { password: data.password } : {}),
      });
      queryClient.invalidateQueries({ queryKey: ['allUsers'] });
      toast.success(res?.msg || 'Erfolgreich erstellt!');
      setShowSlideIn(false); // Close the NewUserSlideIn when successful
    } catch (error) {
      toast.error(error?.response?.data?.msg);
    }
  };

  return (
    <>
      <NewUserSlideIn
        showSlideIn={showSlideIn}
        setShowSlideIn={setShowSlideIn}
        onSubmit={handleCreateUser}
      />

      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Benutzerverwaltung
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {totalUsers} Benutzer gefunden.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-svisit-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
              onClick={() => setShowSlideIn(true)}
            >
              Benutzer hinzufügen
            </button>
          </div>
        </div>

        <AdminListToolbar
          search={search}
          onSearchChange={(value) => setParams({ search: value })}
          searchPlaceholder="Name, E-Mail oder Benutzer-ID …"
          filters={[
            {
              key: 'status',
              label: 'Status',
              value: getParam('status'),
              options: statusFilterOptions('user', 'Alle Stati'),
              onChange: (value) => setParams({ status: value }),
            },
            {
              key: 'role',
              label: 'Rolle',
              value: getParam('role'),
              options: ROLE_OPTIONS,
              onChange: (value) => setParams({ role: value }),
            },
            {
              key: 'isVerified',
              label: 'E-Mail bestätigt',
              value: getParam('isVerified'),
              options: VERIFIED_OPTIONS,
              onChange: (value) => setParams({ isVerified: value }),
            },
            {
              key: 'hasCompany',
              label: 'Firma',
              value: getParam('hasCompany'),
              options: COMPANY_OPTIONS,
              onChange: (value) => setParams({ hasCompany: value }),
            },
          ]}
          onReset={reset}
        />

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <SortableHeader
                      first
                      field="lastName"
                      label="Name"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <SortableHeader
                      field="status"
                      label="Status"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <SortableHeader
                      field="email"
                      label="E-Mail"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      E-Mail bestätigt
                    </th>
                    <SortableHeader
                      field="role"
                      label="Rolle"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Firma
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Karten
                    </th>
                    <SortableHeader
                      field="createdAt"
                      label="Erstellt"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">Bearbeiten</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={COLUMN_COUNT}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        Lädt …
                      </td>
                    </tr>
                  )}

                  {isError && !isLoading && (
                    <tr>
                      <td
                        colSpan={COLUMN_COUNT}
                        className="px-3 py-8 text-center text-sm text-red-600"
                      >
                        Benutzer konnten nicht geladen werden.
                      </td>
                    </tr>
                  )}

                  {!isLoading && !isError && users.length === 0 && (
                    <tr>
                      <td
                        colSpan={COLUMN_COUNT}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        Keine Benutzer gefunden.
                      </td>
                    </tr>
                  )}

                  {users.map((user) => (
                    <tr key={user._id} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {user.firstName} {user.lastName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <StatusBadge type="user" status={user.status} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.isVerified ? (
                          <>
                            <CheckIcon
                              className="h-4 w-4 text-green-600"
                              aria-hidden="true"
                            />
                            <span className="sr-only">bestätigt</span>
                          </>
                        ) : (
                          <span className="sr-only">offen</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.role}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.companyName || user.organization || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.cardCount > 0 ? (
                          <Link
                            to={`/dashboard/kartenadministration?owner=${user._id}`}
                            className="text-svisit-primary-600 hover:text-svisit-primary-900"
                          >
                            {user.cardCount}
                          </Link>
                        ) : (
                          '0'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <Link
                          to={`/dashboard/benutzerverwaltung/${user._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          Bearbeiten
                          <span className="sr-only">
                            , {user.firstName} {user.lastName}
                          </span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={page}
                numOfPages={numOfPages}
                total={totalUsers}
                limit={limit}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserList;
